import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcFundingV1TransferQueryServiceTransferGet } from '@interxfi/api-client/generated/hooks/ixc-funding-v1-transfer-query-service-transfer-get';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import { Typography } from '@interxfi/design-system/components/typography';
import { getFundingSourceDetailPath } from '../../../../../../../../../../../features/funding/source/funding-source-routing';
import { FundingTransferEntityMenu } from '../../../../../../../../../../../features/funding/transfer/funding-transfer-entity-menu';
import { FundingTransferEventSummary } from '../../../../../../../../../../../features/funding/transfer/funding-transfer-event';
import { getFundingTransferDetailPageListItems } from '../../../../../../../../../../../features/funding/transfer/funding-transfer-list-items';
import { getFundingTransferDetailPath } from '../../../../../../../../../../../features/funding/transfer/funding-transfer-routing';
import { FundingTransferStatusBanner } from '../../../../../../../../../../../features/funding/transfer/funding-transfer-status-banner';
import { FundingTransferStatusPill } from '../../../../../../../../../../../features/funding/transfer/funding-transfer-status-pill';
import { FundingTransferSummaryBar } from '../../../../../../../../../../../features/funding/transfer/funding-transfer-summary-bar';
import { FundingTransferTypePill } from '../../../../../../../../../../../features/funding/transfer/funding-transfer-type-pill';
import { RegistrationAccountPageHeader } from '../../../../../../../../../../../features/registration/account/registration-account-page-header';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/registration/account/$accountId/_account/_detail/funding/_funding/transfer/$transferId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `account-funding-transfer-${params.transferId}`,
        label: 'Funding Transfer',
        to: getFundingTransferDetailPath(params.transferId, params.accountId),
        suffix: (
          <CopyButton content={params.transferId} label="Copy Transfer ID" tooltip={{ copy: params.transferId, copied: 'Copied Transfer ID' }} />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Funding Transfer'),
  component: AccountFundingTransferDetail,
});

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load transfer', 'view this transfer');

function AccountFundingTransferDetail() {
  const { accountId, transferId } = Route.useParams();
  const request = { transferId } as const;
  const { data, error, isPending } = useIxcFundingV1TransferQueryServiceTransferGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(
    () =>
      getFundingTransferDetailPageListItems(data?.transfer, {
        sourceId: getFundingSourceDetailPath(data?.transfer?.sourceId, accountId),
        accountId: true,
        programId: true,
      }),
    [data?.transfer, accountId],
  );

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <VerticalStack gap={7}>
          <FundingTransferStatusBanner transferId={transferId} />

          <RegistrationAccountPageHeader
            accountId={accountId}
            actions={<FundingTransferEntityMenu transferId={transferId} />}
            pills={
              <>
                <FundingTransferStatusPill isPending={showPending} size="large" status={data?.transfer?.status} />
                <FundingTransferTypePill isPending={showPending} size="large" type={data?.transfer?.data?.spec.transferType} />
              </>
            }
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <FundingTransferSummaryBar transferId={transferId} />
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <FundingTransferEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.general.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
